<template>
  <main class="wrapper-content">
    <h1>{{ $t('roles') }}</h1>
    <template v-if="!isLoading">
      <b-card class="border">
        <b-row>
          <LmTextInput
            class="col-12"
            :label="$t('name')"
            :placeholder="$t('enter_name')"
            v-model="$v.form.name.$model"
            :v="v('form.name')"
          />
          <LmTextArea
            class="col-12"
            :label="$t('description')"
            :placeholder="$t('enter_description')"
            v-model="form.description"
            :v="v('form.description')"
          />
          <LmColorPicker
            class="col-md-6 col-12"
            label="Color"
            :swatches="swatches"
            :showFallback="true"
            v-model="form.color"
            :rowLength="10"
            :v="v('form.color')"
          />
        </b-row>
      </b-card>
      <LmDatatableWithModalSelector
        datatableSlug="privilegios-modal-internal"
        datatableModalSlug="privilegios-modal-internal"
        @itemsAdded="onAddPrivileges"
        @itemsToRemove="onRemovePrivileges"
        :hideTableFilter="true"
        :hideTableButtons="false"
        :items="form.privileges"
        :addItemButtonLabel="$t('add')"
        :emptyItemsMessage="$t('no_privileges_selected')"
        :removeConfirmationMessage="$t('privileges_remove_confirmation')"
      />
    </template>
    <b-row v-else>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col md="12" class="my-4">
        <div>
          <div class="d-flex flex-wrap mb-3" style="gap: 1rem">
            <span
              class="p-2 border rounded-lg d-flex align-items-center justify-content-between"
              style="gap: 1rem"
              v-for="(item, index) in 4"
              :key="index"
            >
              <b-skeleton type="avatar"></b-skeleton>
              <b-skeleton width="100px"></b-skeleton>
            </span>
          </div>
          <b-skeleton type="button" width="150px"></b-skeleton>
        </div>
      </b-col>
    </b-row>
    <div class="buttons">
      <LmButton
          buttonClass="btn-secondary"
          :value="$t('go_back')"
          @click="onGoBack"
          :isProcessing="isProcessing"
        />
        <LmButton
          buttonClass="btn-primary"
          :value="$t('save')"
          @click="onSubmit"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-if="isOnEditMode && !deleted"
          :requireConfirmation="true"
          :confirmationTitle="$t('you_are_about_to_delete')"
          buttonClass="btn-danger"
          :value="$t('delete')"
          @click="onDelete"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-if="isOnEditMode && deleted"
          value="Reactivar"
          :requireConfirmation="true"
          :confirmationTitle="$t('you_are_about_to_reactivate')"
          buttonClass="btn-success"
          @click="onReactivate()"
          :isProcessing="isProcessing"
        />
    </div>
  </main>
</template>

<script>
// Components
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmButton from '@/components/form/elements/LmButton'
import LmTextArea from '@/components/form/elements/LmTextArea'
import LmDatatableWithModalSelector from '@/components/LmDatatableWithModalSelector'
import LmColorPicker from '@/components/form/elements/LmColorPicker'

// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts'
import _ from 'lodash'
import { required } from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  components: {
    LmDatatableWithModalSelector,
    LmTextInput,
    LmTextArea,
    LmButton,
    LmColorPicker
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    frontendRouteToRedirectAfterCreated: {
      type: String,
      required: true
    },
    serviceEndpoint: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isLoading: false,
      swatches: [
        ['#F6648B', '#F493A7', '#F891A6', '#FFCCD5', '#FF9999', '#FF6666', '#FF3333', '#CC0000', '#990000', '#660000'],
        ['#a27bff', '#b99cff', '#d0bdff', '#e8deff', '#b380ff', '#9966cc', '#8040a0', '#663380', '#4d2255', '#33112b'],
        ['#74ebe3', '#96f0ea', '#b9f5f1', '#dcfaf8', '#66ccff', '#4da6cc', '#3380a0', '#225580', '#112b4d', '#001122'],
        ['#ffb748', '#ffc976', '#ffdba3', '#ffedd1', '#ffcc66', '#ffaa33', '#ff8800', '#cc6600', '#994400', '#662200']
      ],
      form: {
        name: '',
        description: '',
        privileges: [],
        color: ''
      },
      deleted: false,
      isProcessing: false
    }
  },
  validations: {
    form: {
      name: { required }
    }
  },
  methods: {
    getRandomColor () {
      const mainArrayIndex = Math.floor(Math.random() * this.swatches.length)
      const subArrayIndex = Math.floor(Math.random() * this.swatches[mainArrayIndex].length)
      const randomColor = this.swatches[mainArrayIndex][subArrayIndex]
      return randomColor
    },
    v (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    },
    async onSubmit () {
      try {
        await this.validateForm(this.$v)
        this.isProcessing = true
        if (this.isOnEditMode) {
          await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`, this.form)
        } else {
          await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}`, this.form)
        }
        this.lmToastSuccessMessage(
          `${this.$t('saved_successfully')} '${this.form.name}'`,
          `${this.$t('action_done_successfully')}`
        )
        this.onGoBack()
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    onGoBack () {
      this.$router.go(-1)
    },
    async onDelete () {
      try {
        this.isProcessing = true
        await commonsApi.axiosDeleteWrapper(`/${this.serviceEndpoint}/delete/${this.$route.params.id}`)
        this.lmToastSuccessMessage(
          `${this.$t('was_successfully_deleted')} ${this.name}`,
          `${this.$t('action_done_successfully')}`
        )
        this.onGoBack()
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async onReactivate () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/reactivate/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`${this.$t('was_successfully_reactivated')}`, `${this.$t('it_was_successfully_reactivated')}`)
        this.onGoBack()
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async loadData () {
      const res = await commonsApi.axiosGetWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`)
      this.form = res.data
    },

    onAddPrivileges (items) {
      try {
        if (items?.length) {
          this.form.privileges = items
        }
      } catch (error) {
        console.log(error)
      }
    },

    onRemovePrivileges (items) {
      try {
        if (items?.length) {
          const idsToRemove = items.map(i => i.id)
          this.form.privileges = this.form.privileges.filter(obj => !idsToRemove.includes(obj.id))
        }
      } catch (error) {
        console.log(error)
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      if (this.isOnEditMode) {
        await this.loadData()
      } else {
        this.form.color = this.getRandomColor()
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>
